import { Business } from '../types/business';

// "Is this business open right now?" is answered in the BUSINESS's local time,
// not the visitor's: someone browsing from overseas (or from Perth, looking at
// a Sydney listing) must still see the right answer.
//
// Stored working hours come in a few shapes, all handled here:
//   keys:    "mon" / "monday" / "Monday"
//   value:   { open, close } or [{ open, close }, ...] (several periods a day)
//   times:   "9:00 am", "09:00", "5pm", "24h", "Closed"
//   flags:   optional isOpen / closed / status

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const CITY_TIME_ZONES: Record<string, string> = {
    sydney: 'Australia/Sydney',
    newcastle: 'Australia/Sydney',
    wollongong: 'Australia/Sydney',
    canberra: 'Australia/Sydney',
    melbourne: 'Australia/Melbourne',
    brisbane: 'Australia/Brisbane',
    'gold coast': 'Australia/Brisbane',
    southport: 'Australia/Brisbane',
    adelaide: 'Australia/Adelaide',
    perth: 'Australia/Perth',
    darwin: 'Australia/Darwin',
    hobart: 'Australia/Hobart',
    tasmania: 'Australia/Hobart',
};
const STATE_TIME_ZONES: Array<[RegExp, string]> = [
    [/\b(NSW|ACT|New South Wales)\b/i, 'Australia/Sydney'],
    [/\b(VIC|Victoria)\b/i, 'Australia/Melbourne'],
    [/\b(QLD|Queensland)\b/i, 'Australia/Brisbane'],
    [/\b(SA|South Australia)\b/, 'Australia/Adelaide'],
    [/\b(WA|Western Australia)\b/, 'Australia/Perth'],
    [/\b(NT|Northern Territory)\b/, 'Australia/Darwin'],
    [/\b(TAS|Tasmania)\b/i, 'Australia/Hobart'],
];

/** The time zone the business keeps its hours in. Defaults to Sydney. */
export const businessTimeZone = (business: Business): string => {
    const b = business as any;
    if (/iran|^IR$|ایران/i.test(String(b.country || ''))) return 'Asia/Tehran';
    const city = String(b.city?.name || b.customCity || '').trim().toLowerCase();
    if (CITY_TIME_ZONES[city]) return CITY_TIME_ZONES[city];
    const address = String(b.address || '');
    const byState = STATE_TIME_ZONES.find(([pattern]) => pattern.test(address));
    return byState ? byState[1] : 'Australia/Sydney';
};

/** Day of week (0 = Sunday) and minutes since midnight, in the given time zone. */
const nowInTimeZone = (timeZone: string, at: Date = new Date()): { day: number; minutes: number } => {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone, weekday: 'long', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        }).formatToParts(at);
        const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
        const day = DAY_KEYS.indexOf(get('weekday').toLowerCase());
        const minutes = (parseInt(get('hour'), 10) % 24) * 60 + parseInt(get('minute'), 10);
        if (day >= 0 && !Number.isNaN(minutes)) return { day, minutes };
    } catch {
        // Unknown time zone or no Intl support - fall through to the device clock.
    }
    return { day: at.getDay(), minutes: at.getHours() * 60 + at.getMinutes() };
};

/** "9:00 am" / "17:30" / "5pm" -> minutes since midnight; null if unreadable. */
const parseTimeToMinutes = (value: unknown): number | null => {
    const m = String(value ?? '').trim().match(/^(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?$/i);
    if (!m) return null;
    let hour = parseInt(m[1] || '', 10);
    const minute = parseInt(m[2] || '0', 10);
    const meridiem = (m[3] || '').toLowerCase();
    if (meridiem === 'pm' && hour < 12) hour += 12;
    if (meridiem === 'am' && hour === 12) hour = 0;
    if (Number.isNaN(hour) || hour > 24 || minute > 59) return null;
    return hour * 60 + minute;
};

/** The opening periods for one day, as [startMinutes, endMinutes] pairs. */
const periodsForDay = (workingHours: any, dayIndex: number): Array<[number, number]> => {
    if (!workingHours || typeof workingHours !== 'object') return [];
    const full = DAY_KEYS[dayIndex] || '';
    const key = Object.keys(workingHours).find((k) => {
        const lower = k.toLowerCase();
        return lower === full || lower === full.slice(0, 3);
    });
    const raw = key ? workingHours[key] : null;
    const entries: any[] = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const periods: Array<[number, number]> = [];
    entries.forEach((entry) => {
        if (!entry || entry.isOpen === false || entry.closed === true || entry.status === 'closed') return;
        const open = String(entry.open ?? '').trim().toLowerCase();
        const close = String(entry.close ?? '').trim().toLowerCase();
        if (/^24\s*(h|hours?)$/.test(open) || /^24\s*(h|hours?)$/.test(close)) {
            periods.push([0, 24 * 60]);
            return;
        }
        const start = parseTimeToMinutes(open);
        const end = parseTimeToMinutes(close);
        if (start === null || end === null) return; // "Closed" or unreadable
        // 00:00-00:00 / 00:00-23:59 / 12:00 am-11:59 pm all mean "all day".
        if (start === 0 && (end === 0 || end >= 23 * 60 + 59)) {
            periods.push([0, 24 * 60]);
            return;
        }
        periods.push([start, end]);
    });
    return periods;
};

export const hasWorkingHours = (business: Business): boolean =>
    Boolean(business.workingHours && Object.keys(business.workingHours).length > 0);

export const isBusinessOpen = (business: Business, at: Date = new Date()): boolean => {
    if (!hasWorkingHours(business)) return false;
    const { day, minutes } = nowInTimeZone(businessTimeZone(business), at);

    const today = periodsForDay(business.workingHours, day);
    if (today.some(([start, end]) => (
        end > start ? minutes >= start && minutes < end : minutes >= start // closes after midnight
    ))) return true;

    // Still inside a period that started yesterday and runs past midnight (e.g. 10 pm - 2 am).
    const yesterday = periodsForDay(business.workingHours, (day + 6) % 7);
    return yesterday.some(([start, end]) => end < start && minutes < end);
};

export const getBusinessStatus = (business: Business): { label: string; color: string; type: string } => {
    if (business.listingType === 'Online' || business.category?.name === 'Online Service') {
        return { label: 'Online Service', color: '#3b82f6', type: 'online' };
    }

    if (!hasWorkingHours(business)) {
        return { label: 'By Appointment', color: '#94a3b8', type: 'appointment' };
    }

    if (isBusinessOpen(business)) {
        return { label: 'Open Today', color: '#10b981', type: 'open' };
    }

    const { day, minutes } = nowInTimeZone(businessTimeZone(business));
    const today = periodsForDay(business.workingHours, day);
    if (today.length === 0) {
        return { label: 'Closed Today', color: '#ef4444', type: 'closed' };
    }

    // Not open right now, but opens later today: say when.
    const upcoming = today.map(([start]) => start).filter((start) => start > minutes).sort((a, b) => a - b)[0];
    if (upcoming !== undefined) {
        let hours = Math.floor(upcoming / 60);
        const mins = upcoming % 60;
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        const timeStr = `${hours}${mins > 0 ? ':' + String(mins).padStart(2, '0') : ''} ${ampm}`;
        return { label: `Opens at ${timeStr}`, color: '#f59e0b', type: 'upcoming' };
    }

    return { label: 'Closed Now', color: '#ef4444', type: 'closed' };
};
